import React from "react"
import { Form, Formik, FormikHelpers } from "formik"
import * as Yup from "yup"
import { toast } from "react-toastify"
import { AxiosError } from "axios"
import { useNavigate } from "react-router-dom"
import MyAlert from "@common-utils/MyAlert"
import MySimpleSpinner from "@common-utils/MySimpleSpinner"
import MyInputFormField from "@common-components/fields/MyInputFormField"
import { IHttpErrorResponseModel } from "@common-models/*"
import { CONST_HTTP_CUSTOM_CODE_FORM_VALIDATION_ERROR } from "@common-constants/*"
import { useAuth } from "../../AppContext"
import { changeMyPasswordRequest } from "./core/_requests"

interface IForm {
     password?: string
     passwordConfirm?: string
}

// Écran de changement de mot de passe FORCÉ : affiché tant que le mot de passe est provisoire
// (mustChangePassword). Après enregistrement, le drapeau est levé côté serveur et dans le contexte.
const ForceChangePassword = () => {
     const navigate = useNavigate()
     const { setCurrentUser } = useAuth()

     const schema = Yup.object().shape({
          password: Yup.string().min(6, "Au moins 6 caractères.").required().label(`"Mot de passe"`),
          passwordConfirm: Yup.string()
               .oneOf([Yup.ref("password")], "Les mots de passe ne correspondent pas.")
               .required()
               .label(`"Confirmation"`),
     })

     const initialValues: IForm = { password: "", passwordConfirm: "" }

     function handleSubmit(values: IForm, helpers: FormikHelpers<IForm>) {
          helpers.setStatus(null)
          changeMyPasswordRequest(values.password as string)
               .then(() => {
                    toast.success("Votre mot de passe a été mis à jour.")
                    setCurrentUser(prev => prev && { ...prev, mustChangePassword: false })
                    navigate("/dashboard")
               })
               .catch((e: AxiosError) => {
                    const error: IHttpErrorResponseModel = e.response?.data
                    if (error?.code === CONST_HTTP_CUSTOM_CODE_FORM_VALIDATION_ERROR && error?.errors) {
                         for (const key in error.errors) helpers.setFieldError(key, error.errors[key])
                    }
                    helpers.setStatus(error?.detail)
                    toast.error(error?.detail, { autoClose: false })
                    helpers.setSubmitting(false)
               })
     }

     return (
          <div className={"d-flex justify-content-center"}>
               <div className={"w-100 w-lg-75 w-xxl-50"}>
                    <MyAlert title={<>Changement de mot de passe requis</>} type={"primary"} classNames={"mb-5"}>
                         Votre mot de passe est provisoire. Pour des raisons de sécurité, merci de définir un nouveau mot de passe avant d'accéder à
                         l'application.
                    </MyAlert>

                    <div className="card">
                         <div className="card-body">
                              <Formik initialValues={initialValues} onSubmit={handleSubmit} validationSchema={schema}>
                                   {helpers => (
                                        <Form noValidate autoComplete="off">
                                             {helpers.status && (
                                                  <MyAlert type={"danger"} classNames={"mb-4"}>
                                                       {helpers.status}
                                                  </MyAlert>
                                             )}

                                             <MyInputFormField
                                                  name={"password"}
                                                  type={"password"}
                                                  formikProps={helpers}
                                                  validationSchema={schema}
                                                  label={"Nouveau mot de passe"}
                                                  placeholder={"Nouveau mot de passe"}
                                                  classNameLayout={"mb-4"}
                                             />

                                             <MyInputFormField
                                                  name={"passwordConfirm"}
                                                  type={"password"}
                                                  formikProps={helpers}
                                                  validationSchema={schema}
                                                  label={"Confirmer le mot de passe"}
                                                  placeholder={"Confirmer le mot de passe"}
                                                  classNameLayout={"mb-4"}
                                             />

                                             <div className="d-flex justify-content-end">
                                                  <button type={"submit"} className={"btn btn-primary btn-sm"} disabled={helpers.isSubmitting}>
                                                       {!helpers.isSubmitting ? <>Enregistrer</> : <MySimpleSpinner size={"sm"} />}
                                                  </button>
                                             </div>
                                        </Form>
                                   )}
                              </Formik>
                         </div>
                    </div>
               </div>
          </div>
     )
}

export default ForceChangePassword
